/** 簡易 API client：統一 `{ data } / { error }` 格式、帶上 cookie。 */
export interface Me {
  userId: string;
  displayName: string | null;
  email: string | null;
  roles: string[];
  permissions: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const body = (await res.json()) as { data?: T; error?: string };
  if (!res.ok || body.error) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body.data as T;
}

export interface ArticleDetail {
  article: ArticleRow & {
    bodyJson: string | null;
    bodyHtmlLegacy: string | null;
    excerpt: string | null;
    coverMediaId: string | null;
  };
  coverUrl: string | null;
  seo: Record<string, unknown> | null;
  relatedIds: string[];
  categoryIds: string[];
  tagIds: string[];
  tags: string[];
  /** 前台網域，用來組正式發佈連結。 */
  webBaseUrl: string;
}

export interface SavePayload {
  patch?: Record<string, unknown>;
  seo?: Record<string, unknown>;
  relatedIds?: string[];
  categoryIds?: string[];
  tags?: string[];
}

export interface MediaRow {
  id: string;
  url: string;
  mime: string | null;
  title: string | null;
  alt: string | null;
  width: number | null;
  height: number | null;
  createdAt: number;
  /** 最近一次被文章刊出的時間（沒被用過為 null）。 */
  publishedAt: number | null;
  articleCount: number;
}

export const api = {
  me: () => request<Me | null>('/auth/me'),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  identities: () => request<{ provider: string; email: string | null }[]>('/auth/identities'),
  unlinkProvider: (p: string) => request<{ ok: boolean }>(`/auth/${p}/unlink`, { method: 'POST' }),
  listArticles: (params: Record<string, string>) =>
    request<{ items: ArticleRow[]; total: number }>(
      `/articles?${new URLSearchParams(params).toString()}`,
    ),
  getArticle: (id: string) => request<ArticleDetail>(`/articles/${id}`),
  articlesByIds: (ids: string[]) =>
    request<{ items: ArticleRow[] }>(`/articles?ids=${ids.join(',')}`),
  createArticle: (title: string) =>
    request<{ id: string }>('/articles', { method: 'POST', body: JSON.stringify({ title }) }),
  saveArticle: (id: string, payload: SavePayload) =>
    request<{ ok: boolean }>(`/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteArticle: (id: string) =>
    request<{ ok: boolean }>(`/articles/${id}`, { method: 'DELETE' }),
  publishArticle: (id: string) => request<{ ok: boolean }>(`/articles/${id}/publish`, { method: 'POST' }),
  previewToken: (id: string) =>
    request<{ url: string }>(`/articles/${id}/preview-token`, { method: 'POST' }),
  categories: () => request<{ id: string; name: string; slug: string }[]>('/categories'),
  tags: (opts?: { q?: string; limit?: number }) => {
    const p = new URLSearchParams();
    if (opts?.q) p.set('q', opts.q);
    if (opts?.limit) p.set('limit', String(opts.limit));
    const qs = p.toString();
    return request<{ id: string; name: string; slug: string }[]>(`/tags${qs ? `?${qs}` : ''}`);
  },
  users: () => request<{ id: string; displayName: string }[]>('/users'),
  usersManage: () =>
    request<
      {
        id: string;
        displayName: string;
        email: string | null;
        status: string;
        providers: string | null;
        roles: string | null;
      }[]
    >('/users/manage'),
  setUserRole: (id: string, roleId: string) =>
    request<{ ok: boolean }>(`/users/${id}/role`, { method: 'POST', body: JSON.stringify({ roleId }) }),
  setUserEmail: (id: string, email: string) =>
    request<{ ok: boolean }>(`/users/${id}/email`, { method: 'POST', body: JSON.stringify({ email }) }),
  userBindLink: (id: string) => request<{ url: string }>(`/users/${id}/bind-link`, { method: 'POST' }),
  mediaList: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ items: MediaRow[]; total: number }>(`/media/list${qs ? `?${qs}` : ''}`);
  },
  mediaUsage: (id: string) =>
    request<{ items: { id: string; title: string; slug: string; publishedAt: number | null }[] }>(
      `/media/${id}/usage`,
    ),
  invite: (email: string, roleId: string, send: boolean) =>
    request<{ acceptUrl: string; emailed: boolean; emailError?: string }>('/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, roleId, send }),
    }),
  uploadImage: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/media', { method: 'POST', credentials: 'include', body: fd });
    const body = (await res.json()) as { data?: { id: string; url: string }; error?: string };
    if (!res.ok || body.error) throw new Error(body.error ?? 'upload failed');
    return body.data!;
  },
};

export interface ArticleRow {
  id: string;
  title: string;
  slug: string;
  status: string;
  authorId?: string | null;
  authorName?: string | null;
  coverUrl?: string | null;
  categoryNames?: string | null;
  publishedAt: number | null;
  scheduledAt?: number | null;
  updatedAt: number;
}

/** 觸發 provider 登入（整頁導向）。 */
export function loginWith(provider: 'line' | 'yahoo' | 'google'): void {
  window.location.href = `/api/auth/${provider}/login`;
}

/** 連結其他登入方式到目前帳號（整頁導向）。 */
export function linkWith(provider: 'line' | 'yahoo' | 'google'): void {
  window.location.href = `/api/auth/${provider}/link`;
}
